import React, { ReactNode, useRef } from "react";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";

import SidebarView, { SidebarRef } from "../Sidebar";
import Headerbar from "../Headerbar";

interface AppLayoutProps {
  /** Used by Sidebar (active item) and Headerbar (breadcrumb fallback). */
  pageName: string;
  children: ReactNode;
  /** Custom header left content (e.g. breadcrumbs). Falls back to auto breadcrumbs. */
  headerLeftContent?: ReactNode;
  /**
   * Max width of the centered content area. Caps line length on wide screens.
   * Set to false to let content span the full width (e.g. data-dense editors).
   */
  maxWidth?: number | string | false;
  /** Remove the default content padding (for full-bleed pages). */
  disableContentPadding?: boolean;
  /** Override the main background color. */
  contentBackground?: string;
  /** Extra styles merged into the centered content container. */
  contentSx?: SxProps<Theme>;
}

const DEFAULT_MAX_WIDTH = 1360;

export default function AppLayout({
  pageName,
  children,
  headerLeftContent,
  maxWidth = DEFAULT_MAX_WIDTH,
  disableContentPadding = false,
  contentBackground,
  contentSx,
}: AppLayoutProps) {
  const theme = useTheme();
  const sidebarRef = useRef<SidebarRef>(null);

  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  return (
    <Box sx={{ display: "flex" }}>
      <SidebarView ref={sidebarRef} pageName={pageName} />
      <Box
        component="main"
        sx={{
          backgroundColor:
            contentBackground ?? theme.palette.background.default,
          flexGrow: 1,
          minWidth: 0,
          height: "100vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Headerbar
          pageName={pageName}
          headerLeftContent={headerLeftContent}
          onMobileMenuClick={handleMobileMenuToggle}
        />

        <Box
          sx={{
            flex: 1,
            width: "100%",
            maxWidth: maxWidth === false ? "none" : maxWidth,
            mx: "auto",
            px: disableContentPadding ? 0 : { xs: 2, md: 3 },
            py: disableContentPadding ? 0 : { xs: 2, md: 3 },
            ...contentSx,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
